export const FAL_NON_REPLAYABLE_HEADER = 'x-glowghost-fal-replay-policy';
export const FAL_NON_REPLAYABLE_VALUE = 'never';
// The worker sets this reason on a submit it refused before contacting fal (its own rate limit or
// missing credentials). Mirrors FAL_NOT_FORWARDED_REASON in worker/src/falProxyPolicy.js.
export const FAL_NOT_FORWARDED_REASON = 'fal_request_not_forwarded';

function createFalSubmissionError(response, body, { viaProxy }) {
  const message = body?.message
    || body?.detail
    || response.statusText
    || `HTTP ${response.status}`;
  const error = new Error(
    typeof message === 'string' ? message : JSON.stringify(message),
  );
  error.name = 'FalSubmissionError';
  error.status = response.status;
  error.body = body;
  // Only our own worker can vouch that nothing reached fal; a direct fal response never can.
  error.submissionNotForwarded = viaProxy && body?.reason === FAL_NOT_FORWARDED_REASON;
  return error;
}

/**
 * Dispatch one FAL queue submission against exactly one resolved transport.
 * Authentication and environment resolution remain owned by config.js.
 */
export async function dispatchFalQueueSubmissionOnce({
  endpointId,
  input,
  signal,
  baseFetch,
  primaryProxyUrl,
  accessToken,
  directCredentials,
  allowDirectCredentials,
  onDispatch,
}) {
  const normalizedEndpointId = typeof endpointId === 'string'
    ? endpointId.trim().replace(/^\/+|\/+$/g, '')
    : '';
  if (!/^[a-z0-9_-]+(?:\/[a-z0-9_.-]+)+$/i.test(normalizedEndpointId)) {
    throw new Error('A valid FAL queue endpoint ID is required.');
  }

  const targetUrl = `https://queue.fal.run/${normalizedEndpointId}`;
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-fal-queue-priority': 'normal',
  };
  let requestUrl = targetUrl;

  if (primaryProxyUrl) {
    requestUrl = primaryProxyUrl;
    headers['x-fal-target-url'] = targetUrl;
    headers[FAL_NON_REPLAYABLE_HEADER] = FAL_NON_REPLAYABLE_VALUE;
    const resolvedAccessToken = await accessToken;
    if (resolvedAccessToken) {
      headers.Authorization = `Bearer ${resolvedAccessToken}`;
    }
  } else if (allowDirectCredentials && directCredentials) {
    headers.Authorization = `Key ${directCredentials}`;
  } else {
    throw new Error('FAL client is not configured.');
  }

  if (signal?.aborted) throw signal.reason || new Error('FAL submission cancelled before dispatch.');
  onDispatch?.();
  const response = await baseFetch(requestUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
    signal,
  });
  const body = await response.clone().json().catch(() => null);
  if (!response.ok) {
    throw createFalSubmissionError(response, body, { viaProxy: Boolean(primaryProxyUrl) });
  }
  if (!body || typeof body !== 'object') {
    throw new Error('FAL returned an invalid queue submission response.');
  }
  return body;
}
